import { z } from "zod";
import { productRules } from "./rules.js";
import type { Severity, TenantValidationSettings } from "./types.js";

/**
 * Stammdaten-Prüfung je Kunde (nur Logiful stellt ein): welche Prüfregeln Aufträge zurückhalten („hält zurück“ = Fehler,
 * Artikel geht nicht ans Lager), nur Hinweis sind oder aus. Regeln, die das Lager zwingend braucht (SKU, Name, Gewicht,
 * Stückliste, Gebinde) und Gefahrgut sind fest – sonst scheitert die Übertragung oder es wird gefährlich.
 */
export const CONFIGURABLE_VALIDATION_RULES = [
  "gtin.recommended",
  "gtin.checksum",
  "gtin.restricted_prefix",
  "gtin.unique",
  "weight.plausible",
  "weight.density",
  "dimensions.required",
  "dimensions.plausible",
  "customs.hs_code",
  "customs.origin_country",
  "customs.value",
  "price.non_negative",
] as const;
export type ConfigurableValidationRule = (typeof CONFIGURABLE_VALIDATION_RULES)[number];
export type RuleLevel = "error" | "warning" | "off";

export const validationSettingsInputSchema = z.object({
  /** Zolldaten (HS-Code, Ursprung, Warenwert) überhaupt prüfen – Kunde versendet in Drittländer */
  shipsOutsideEu: z.boolean(),
  /** je Regel: hält zurück / nur Hinweis / aus (fehlend = Standard) */
  rules: z.partialRecord(z.enum(CONFIGURABLE_VALIDATION_RULES), z.enum(["error", "warning", "off"])).default({}),
});
export type ValidationSettingsInput = z.infer<typeof validationSettingsInputSchema>;

export const defaultRuleSeverity = (id: string): Severity => productRules.find((r) => r.id === id)?.defaultSeverity ?? "warning";

/** wirksame Stufe einer Regel beim Kunden */
export function ruleLevel(id: string, s: TenantValidationSettings): RuleLevel | "info" {
  const o = s.ruleOverrides?.[id];
  if (o?.enabled === false) return "off";
  const sev = o?.severity ?? defaultRuleSeverity(id);
  return sev;
}

/** Eingabe → gespeicherte Einstellung; nur Abweichungen vom Standard werden gespeichert */
export function toTenantValidationSettings(input: ValidationSettingsInput, prev: TenantValidationSettings): TenantValidationSettings {
  const overrides: NonNullable<TenantValidationSettings["ruleOverrides"]> = {};
  // nicht einstellbare Regeln: bestehende Überschreibungen (falls je gesetzt) bleiben unangetastet
  for (const [id, o] of Object.entries(prev.ruleOverrides ?? {})) if (!(CONFIGURABLE_VALIDATION_RULES as readonly string[]).includes(id)) overrides[id] = o;
  for (const [id, level] of Object.entries(input.rules)) {
    if (!level) continue;
    if (level === "off") overrides[id] = { enabled: false };
    // „nur Hinweis“ deckt Hinweis und Info ab – bei Info-Standard keine Überschreibung
    else if (level !== defaultRuleSeverity(id) && !(level === "warning" && defaultRuleSeverity(id) === "info")) overrides[id] = { severity: level };
  }
  return { shipsOutsideEu: input.shipsOutsideEu, ...(Object.keys(overrides).length ? { ruleOverrides: overrides } : {}) };
}
